import dayjs, { Dayjs } from 'dayjs';

export const apiIsoStringFormat = 'YYYY-MM-DDTHH:mm:ss.000+00:00';

export const apiTimeStringFormat = 'HH:mm';

export function dayjsNow() {
  return dayjs().local();
}

/**
 *
 * @param input
 * @returns
 */
export function parseDayJs(input: number | string | undefined | null) {
  if (!input) return null;
  if (typeof input === 'string') {
    if (input.includes('+')) {
      return dayjs(input.split('+')[0]).local();
    }
    if (input.match(/^\d{2}:\d{2}$/)) {
      return dayjs(input, apiTimeStringFormat).local();
    }
  }
  return dayjs(input).local();
}

export function parseDayJsRange(input: string | null | undefined) {
  if (!input) return [null, null];

  const range = input.split('-');
  const startRangeDateTime = range[0];
  const startRange = startRangeDateTime.split(' ');
  const endRangeTime = range[1]?.trim();

  const startRangeDayjs = parseDayJs(startRangeDateTime);
  let endRangeDayjs = null;

  if (endRangeTime) {
    const startRangeDate = startRange[0];
    endRangeDayjs = parseDayJs(`${startRangeDate} ${endRangeTime}`);
  }

  return [startRangeDayjs, endRangeDayjs];
}

/**
 * Helper function to parse user input in the DateTimePicker when they enter a value that isn't a full date time
 * but the rest can be inferred (e.g. "MM/DD" or "MM/DD/YYYY hh:mm"). If we cannot infer a valid date time from
 * the input, returns null to clear the field and mark it as invalid.
 * @param inputValue The raw string input from the user in the DateTimePicker
 * @returns A Dayjs object representing the parsed date and time, or null if the input is invalid or incomplete
 */
export function parseDayJsFromDateInput(inputValue: string): Dayjs | null {
  const splitDateAndTime = inputValue.trim().split(' ');
  const datePart = splitDateAndTime[0].split('/');

  // If the user didn't enter at least a month and day, clear the field as invalid
  if (datePart[0] === 'MM' || datePart[1] === 'DD') {
    return null;
  }

  const year = datePart[2] === 'YY' ? dayjsNow().year() : Number(datePart[2]) + 2000;

  const normalized = dayjs()
    .month(Number(datePart[0]) - 1)
    .date(Number(datePart[1]))
    .year(year)
    .hour(0)
    .minute(0)
    .second(0)
    .millisecond(0);

  return normalized.isValid() ? normalized : null;
}

/**
 * Helper function to parse user input in the DateTimePicker when they enter a value that isn't a full date time
 * but the rest can be inferred (e.g. "MM/DD" or "MM/DD/YYYY hh:mm"). If we cannot infer a valid date time from
 * the input, returns null to clear the field and mark it as invalid.
 * @param inputValue The raw string input from the user in the DateTimePicker
 * @returns A Dayjs object representing the parsed date and time, or null if the input is invalid or incomplete
 */
export function parseDayJsFromDateTimeInput(inputValue: string): Dayjs | null {
  const splitDateAndTime = inputValue.trim().split(' ');
  const datePart = splitDateAndTime[0].split('/');
  const timePart = splitDateAndTime[1]?.split(':');

  // If the user didn't enter at least a month and day, clear the field as invalid
  if (datePart[0] === 'MM' || datePart[1] === 'DD') {
    return null;
  }

  const year = datePart[2] === 'YY' ? dayjsNow().year() : Number(datePart[2]) + 2000;
  const hour = timePart[0] !== 'hh' ? Number(timePart[0]) : 0;
  const min = timePart[1] !== 'mm' ? Number(timePart[1]) : 0;

  const normalized = dayjs()
    .month(Number(datePart[0]) - 1)
    .date(Number(datePart[1]))
    .year(year)
    .hour(hour)
    .minute(min)
    .second(0)
    .millisecond(0);

  return normalized.isValid() ? normalized : null;
}
